import type { AxiosError } from 'axios'

import { parseEditDetailedSummaryDepreciationPayload } from '../helpers/parseEditDetailedSummaryDepreciationPayload'
import { parseDetailedSummaryDepreciationPayload } from '../helpers/parseDetailedSummaryDepreciationPayload'
import { parseAdditionalDepreciationPayload } from '../helpers/parseAdditionalDepreciationPayload'
import { toast } from '@utils/functions/notificationToast'
import { useDepreciation } from '.'

import type { AddEditAdditionalDepreciationFormProps } from '../components/modals/AddEditAdditionalDepreciation/form/types'
import type { EditDetailedSummaryDepreciationFormProps } from '../components/modals/EditDetailedSummaryDepreciation/form/types'
import type {
  ApprovalWorkflowDepreciationRequestProps,
  GetBigNumbersDepreciationRequestProps,
  RemoveAdditionalDepreciationRequestProps,
  ReversePreviewDepreciationRequestProps,
  ReverseWorkflowDepreciationRequestProps
} from '@services/api/depreciation/types'
import type { GetAdditionalDepreciationProps, GetDetailedSummaryDepreciationProps } from './types'

import * as apiDepreciation from '@services/api/depreciation'

const reversePreviewDepreciation = async (payload: ReversePreviewDepreciationRequestProps) => {
  const { setIsReversePreviewDepreciationLoading } = useDepreciation.getState()

  setIsReversePreviewDepreciationLoading(true)

  try {
    await apiDepreciation.reversePreviewDepreciation(payload)

    toast.success('Prévia revertida com sucesso!')

    getBigNumbersDepreciation({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível reverter a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReversePreviewDepreciationLoading(false)
  }
}

const releasePreviewDepreciation = async (payload: ReversePreviewDepreciationRequestProps) => {
  const { setIsReleasePreviewDepreciationLoading } = useDepreciation.getState()

  setIsReleasePreviewDepreciationLoading(true)

  try {
    await apiDepreciation.releasePreviewDepreciation(payload)

    toast.success('Prévia liberada com sucesso!')

    getBigNumbersDepreciation({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível liberar a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReleasePreviewDepreciationLoading(false)
  }
}

const reverseWorkflowDepreciation = async (payload: ReverseWorkflowDepreciationRequestProps) => {
  const { setIsReverseWorkflowDepreciationLoading } = useDepreciation.getState()

  setIsReverseWorkflowDepreciationLoading(true)

  try {
    await apiDepreciation.reverseWorkflowDepreciation(payload)

    toast.success('Prévia de depreciação restaurada com sucesso!')

    getBigNumbersDepreciation({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao restaurar a prévia de depreciação!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsReverseWorkflowDepreciationLoading(false)
  }
}

const approvalWorkflowDepreciation = async (payload: ApprovalWorkflowDepreciationRequestProps) => {
  const { setIsApprovalWorkflowDepreciationLoading } = useDepreciation.getState()

  setIsApprovalWorkflowDepreciationLoading(true)

  try {
    await apiDepreciation.approvalWorkflowDepreciation(payload)

    toast.success('Prévia de outras despesas enviada com sucesso!')

    getBigNumbersDepreciation({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao enviar a prévia de depreciação!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsApprovalWorkflowDepreciationLoading(false)
  }
}

const getBigNumbersDepreciation = async (payload: GetBigNumbersDepreciationRequestProps) => {
  const { setBigNumbersDepreciationData, setIsBigNumbersDepreciationLoading } = useDepreciation.getState()

  setIsBigNumbersDepreciationLoading(true)

  try {
    const data = await apiDepreciation.getBigNumbersDepreciation(payload)

    setBigNumbersDepreciationData(data)
  } catch (error) {
    toast.error('Falha ao buscar os números gerais de depreciação!', (error as AxiosError)?.status)
  } finally {
    setIsBigNumbersDepreciationLoading(false)
  }
}

const editDetailedSummaryDepreciation = async (formData: EditDetailedSummaryDepreciationFormProps) => {
  const { setIsEditDetailedSummaryDepreciationLoading } = useDepreciation.getState()

  setIsEditDetailedSummaryDepreciationLoading(true)

  try {
    const payload = parseEditDetailedSummaryDepreciationPayload(formData)

    await apiDepreciation.editDetailedSummaryDepreciation(payload)

    toast.success('Depreciação atualizada com sucesso!')

    getDetailedSummaryDepreciation({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersDepreciation({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    toast.error('Falha ao realizar a atualização!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsEditDetailedSummaryDepreciationLoading(false)
  }
}

const getDetailedSummaryDepreciation = async (formData: GetDetailedSummaryDepreciationProps) => {
  const { setDetailedSummaryDepreciationData, setIsDetailedSummaryDepreciationLoading } = useDepreciation.getState()

  setIsDetailedSummaryDepreciationLoading(true)

  try {
    const payload = parseDetailedSummaryDepreciationPayload(formData)

    const data = await apiDepreciation.getDetailedSummaryDepreciation(payload)

    setDetailedSummaryDepreciationData(data)
  } catch (error) {
    toast.error('Falha ao buscar o detalhamento de depreciação!', (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryDepreciationLoading(false)
  }
}

const ADDITIONAL_DEPRECIATION_SUCCESS_MESSAGE = 'Despesas Adicionais de Depreciação atualizadas com sucesso!'

// backend devolve { errorMessage: string[] } ou o ModelState padrão do ASP.NET ({ errors: { campo: string[] } })
const getApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError<{ errorMessage?: string[]; errors?: Record<string, string[]> }>)?.response?.data

  return data?.errorMessage?.[0] || Object.values(data?.errors || {}).flat()[0] || fallback
}

const getAdditionalDepreciationAccounts = async () => {
  useDepreciation.setState({ isAdditionalDepreciationAccountsLoading: true })

  try {
    const data = await apiDepreciation.getAdditionalDepreciationAccounts()

    useDepreciation.setState({ additionalDepreciationAccounts: data || [] })
  } catch (error) {
    toast.error('Falha ao buscar as contas de depreciação!', (error as AxiosError)?.status)
  } finally {
    useDepreciation.setState({ isAdditionalDepreciationAccountsLoading: false })
  }
}

const getAdditionalDepreciation = async (formData: GetAdditionalDepreciationProps) => {
  const { setAdditionalDepreciationData, setIsAdditionalDepreciationLoading } = useDepreciation.getState()

  useDepreciation.setState({ additionalDepreciationQuery: formData })
  setIsAdditionalDepreciationLoading(true)

  try {
    const data = await apiDepreciation.getAdditionalDepreciation({
      routeParams: { filiCode: formData.filiCode },
      bodyParams: {
        competenceDate: formData.competenceDate,
        currentPage: formData.page!,
        itemsPerPage: formData.itemsPerPage!,
        sortBy: formData.sortType ? formData.sortBy! : null,
        sortType: formData.sortType || null,
        accountingCodeFilter: formData.account && formData.account !== 'Todos' ? [formData.account] : null,
        accountingDescriptionFilter:
          formData.accountDescription && formData.accountDescription !== 'Todos' ? [formData.accountDescription] : null
      }
    })

    setAdditionalDepreciationData(data)
  } catch (error) {
    toast.error('Falha ao buscar as depreciações adicionais!', (error as AxiosError)?.status)
  } finally {
    setIsAdditionalDepreciationLoading(false)
  }
}

// totais e bignumbers são recalculados no backend: basta recarregar a lista (mesmos filtros/página) e o detalhe da prévia
const refreshAfterAdditionalDepreciationChange = (filiCode: string, competenceDate: string) => {
  const { additionalDepreciationQuery } = useDepreciation.getState()

  getAdditionalDepreciation(additionalDepreciationQuery || { filiCode, competenceDate })
  getBigNumbersDepreciation({ filiCode, competenceDate })
}

const saveAdditionalDepreciation = async (formData: AddEditAdditionalDepreciationFormProps) => {
  const { setIsAddEditAdditionalDepreciationLoading } = useDepreciation.getState()
  const isEdit = !!formData.id

  setIsAddEditAdditionalDepreciationLoading(true)

  try {
    const payload = parseAdditionalDepreciationPayload(formData)

    await (isEdit
      ? apiDepreciation.editAdditionalDepreciation(payload)
      : apiDepreciation.createAdditionalDepreciation(payload))

    toast.success(ADDITIONAL_DEPRECIATION_SUCCESS_MESSAGE)

    refreshAfterAdditionalDepreciationChange(formData.filiCode, formData.competenceDate)

    return true
  } catch (error) {
    const fallback = isEdit ? 'Falha ao editar a depreciação!' : 'Falha ao adicionar a depreciação!'

    toast.error(getApiErrorMessage(error, fallback), (error as AxiosError)?.status)

    return false
  } finally {
    setIsAddEditAdditionalDepreciationLoading(false)
  }
}

const removeAdditionalDepreciation = async (payload: RemoveAdditionalDepreciationRequestProps) => {
  const { setIsRemoveAdditionalDepreciationLoading } = useDepreciation.getState()

  setIsRemoveAdditionalDepreciationLoading(true)

  try {
    await apiDepreciation.removeAdditionalDepreciation(payload)

    toast.success(ADDITIONAL_DEPRECIATION_SUCCESS_MESSAGE)

    refreshAfterAdditionalDepreciationChange(payload.filiCode, payload.competenceDate)

    return true
  } catch (error) {
    toast.error(getApiErrorMessage(error, 'Falha ao excluir a depreciação!'), (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveAdditionalDepreciationLoading(false)
  }
}

export {
  getAdditionalDepreciationAccounts,
  getAdditionalDepreciation,
  saveAdditionalDepreciation,
  removeAdditionalDepreciation,
  getDetailedSummaryDepreciation,
  editDetailedSummaryDepreciation,
  getBigNumbersDepreciation,
  reverseWorkflowDepreciation,
  approvalWorkflowDepreciation,
  reversePreviewDepreciation,
  releasePreviewDepreciation
}
